/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLogger } from '@airasia-rn/core-logger';

import { NearestAirportsCodesApiService } from '../services/nearestAirportsCodesApiService';
import { NearestAirportsCodesResponse } from '../types/nearestAirportsCodes.types';
import { useAppConfig } from '@airasia-rn/ui';
import {
  apiErrorMeta,
  classifyServiceError,
  errorTrackingEvent,
} from '../analytics';
import { flightKeys } from './flights';
import { getApiPlatform } from '../util/utils';

export function useNearestAirportsCodesApiService() {
  const appConfig = useAppConfig();
  return useMemo(() => {
    return new NearestAirportsCodesApiService(appConfig.config.env);
  }, [appConfig.config.env]);
}

export const useNearestAirportsCodes = (
  screenName: string = getApiPlatform(),
) => {
  const apiService = useNearestAirportsCodesApiService();
  const { config } = useAppConfig();
  const { logError } = useLogger();

  const query = useQuery<NearestAirportsCodesResponse, Error>({
    queryKey: [...flightKeys.nearestAirportsCodes, config.locale],
    queryFn: ({ signal }) => {
      return apiService.fetchNearestAirports(config.locale, signal);
    },
  });

  // react-query v5 dropped useQuery's onError callback, so error tracking
  // has to observe the query result instead of hooking into the query itself.
  useEffect(() => {
    if (!query.isError) return;

    const { errorType, errorCode, errorDescription, errorMessage } =
      classifyServiceError(query.error);

    logError(
      apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      errorTrackingEvent({
        errorType,
        errorCode,
        errorDescription,
        errorMessage,
        screenName,
        service: apiErrorMeta.SERVICE_NAMES.NEAREST_AIRPORTS,
      }),
    );
  }, [query.isError, query.error, screenName, logError]);

  return query;
};
